import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowsClockwise } from "@phosphor-icons/react";
import api from "../../lib/api";
import { BRAND_META } from "../../context/BrandContext";
import { apiError, btnPrimary, fmtDateTimeWIB, fmtIDR, inp, todayWIB } from "./util";

const STATUS = {
    MATCH: "bg-sage/15 text-sage-dark",
    MISMATCH: "bg-red-50 text-red-700",
    NEEDS_REVIEW: "bg-amber-50 text-amber-700",
};
const JV_LABEL = { MATCH: "COCOK", MISMATCH: "TIDAK COCOK", NEEDS_REVIEW: "PERLU DITINJAU" };
const yesterdayWIB = () => {
    const d = new Date(`${todayWIB()}T00:00:00`);
    d.setDate(d.getDate() - 1);
    return d.toLocaleDateString("en-CA");
};

function Row({ label, value, strong, testId }) {
    return (
        <div className="flex justify-between py-1 text-sm border-b border-mist/60 last:border-0">
            <span className="text-neutral-600">{label}</span>
            <span className={`tabular-nums ${strong ? "font-medium text-ink" : ""}`} data-testid={testId}>{value}</span>
        </div>
    );
}

function BrandResult({ r }) {
    const b = r.buckets;
    return (
        <section className="bg-white border border-mist rounded-sm p-4 md:p-5 space-y-3" data-testid={`jv-brand-${r.brand}`}>
            <div className="flex items-center justify-between gap-2">
                <h3 className="font-serif text-xl">{BRAND_META[r.brand]?.name || r.brand}</h3>
                <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest ${STATUS[r.status]}`} data-testid={`jv-status-${r.brand}`}>
                    {JV_LABEL[r.status] || r.status}
                </span>
            </div>
            <div>
                <Row label="Penjualan produk Ready Stock Internal di Jubelio" value={fmtIDR(r.jubelio_product_sales_idr)} strong testId={`jv-jubelio-${r.brand}`} />
                <Row label={`Penjualan Ready Stock dilaporkan Admin (${r.reports_counted} laporan)`} value={fmtIDR(r.reported_ready_stock_sales_idr)} strong testId={`jv-reported-${r.brand}`} />
                <Row label="Selisih" value={fmtIDR(r.difference_idr)} strong testId={`jv-diff-${r.brand}`} />
                <Row label="Toleransi" value={fmtIDR(r.tolerance_idr)} />
            </div>
            {r.reasons.length > 0 && (
                <ul className="text-xs text-amber-800 bg-amber-50/60 border border-amber-200 rounded-sm p-2.5 space-y-0.5" data-testid={`jv-reasons-${r.brand}`}>
                    {r.reasons.map((x, i) => <li key={i}>{x}</li>)}
                </ul>
            )}
            <div className="text-xs">
                <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-400 mb-1">Dipisahkan (tidak dibandingkan)</div>
                <Row label="Ongkir Internal (order satu brand)" value={fmtIDR(b.internal_shipping_idr)} />
                <Row label="Penjualan marketplace" value={fmtIDR(b.marketplace_sales_idr)} />
                <Row label="Perkiraan payout marketplace (belum uang masuk)" value={fmtIDR(b.marketplace_expected_payout_idr)} />
                <Row label="Dibatalkan" value={fmtIDR(b.cancelled_idr)} />
            </div>
        </section>
    );
}

function Exceptions({ items }) {
    if (!items?.length) return <div className="text-xs text-neutral-400" data-testid="jv-no-exceptions">Tidak ada pengecualian.</div>;
    return (
        <div className="overflow-x-auto border border-mist rounded-sm bg-white" data-testid="jv-exceptions">
            <table className="w-full text-xs">
                <thead className="bg-neutral-50 text-neutral-500">
                    <tr>{["Jenis", "Order / Invoice", "Channel", "Detail", "Nilai", "Memengaruhi Ready Stock"].map((h) => <th key={h} className="text-left font-normal px-3 py-2 whitespace-nowrap">{h}</th>)}</tr>
                </thead>
                <tbody>
                    {items.map((e, i) => (
                        <tr key={i} className="border-t border-mist" data-testid={`jv-exception-${i}`}>
                            <td className="px-3 py-2 whitespace-nowrap">{e.type.replaceAll("_", " ")}</td>
                            <td className="px-3 py-2 font-mono whitespace-nowrap">{e.ref || "—"}{e.invoice_no && e.invoice_no !== e.ref ? ` · ${e.invoice_no}` : ""}</td>
                            <td className="px-3 py-2">{e.source_name || "—"}</td>
                            <td className="px-3 py-2">{e.message}</td>
                            <td className="px-3 py-2 tabular-nums whitespace-nowrap">{e.value_idr === null || e.value_idr === undefined ? "—" : fmtIDR(e.value_idr)}</td>
                            <td className="px-3 py-2">{e.affects_internal ? "Ya" : "Tidak"}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export default function JubelioVerification() {
    const [date, setDate] = useState(yesterdayWIB());
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const load = useCallback(async () => {
        setData(null);
        try {
            const { data } = await api.get("/reporting/jubelio-verification", { params: { reporting_date: date } });
            setData(data);
        } catch (e) {
            toast.error(await apiError(e));
        }
    }, [date]);
    useEffect(() => {
        load();
    }, [load]);
    const refresh = async () => {
        setBusy(true);
        try {
            await api.post("/reporting/jubelio-verification/refresh", { reporting_date: date });
            toast.success("Verifikasi diperbarui dari Jubelio");
            await load();
        } catch (e) {
            toast.error(await apiError(e));
        } finally {
            setBusy(false);
        }
    };
    const v = data?.latest;
    return (
        <div className="space-y-6" data-testid="jubelio-verification">
            <div className="flex flex-wrap items-end gap-3">
                <label className="text-xs text-neutral-500">
                    Tanggal laporan (WIB, satu hari penuh)
                    <input type="date" value={date} max={todayWIB()} onChange={(e) => setDate(e.target.value)} className={`${inp} mt-1 w-48`} data-testid="jv-date-input" />
                </label>
                <button type="button" onClick={refresh} disabled={busy} className={btnPrimary} data-testid="jv-refresh-btn">
                    <ArrowsClockwise size={14} className={busy ? "animate-spin" : ""} /> {busy ? "Membaca Jubelio…" : "Perbarui dari Jubelio"}
                </button>
                <div className="text-xs text-neutral-500" data-testid="jv-last-sync">
                    Sinkronisasi berhasil terakhir: {data?.last_successful_sync ? fmtDateTimeWIB(data.last_successful_sync) : "belum pernah"}
                    {v && ` · ${v.counts.orders} order, ${v.counts.standalone_invoices} invoice tanpa order, ${v.counts.returns} retur · ${v.api_calls} panggilan API`}
                </div>
            </div>
            <p className="text-xs text-neutral-500 max-w-3xl">
                Pemeriksaan hanya-baca. Membandingkan penjualan produk Ready Stock Internal di Jubelio (tanpa ongkir, order batal, PO, dan marketplace) dengan gabungan Laporan Harian yang sudah dikirim per brand. Tidak menyetujui laporan dan tidak mengonfirmasi uang masuk ke rekening.
            </p>
            {data && !v && <div className="text-sm text-neutral-400 border border-dashed border-mist rounded-sm p-6" data-testid="jv-empty">Belum ada verifikasi untuk tanggal ini. Klik Perbarui.</div>}
            {v && (
                <>
                    <div className="grid lg:grid-cols-2 gap-5">{v.results.map((r) => <BrandResult key={r.brand} r={r} />)}</div>
                    <div className="grid sm:grid-cols-3 gap-3 text-xs" data-testid="jv-shared">
                        <div className="bg-white border border-mist rounded-sm p-3">Penjualan Internal brand tidak dikenal<div className="font-serif text-lg mt-1" data-testid="jv-unknown-internal">{fmtIDR(v.unknown_bucket.internal_product_sales_idr)}</div></div>
                        <div className="bg-white border border-mist rounded-sm p-3">Ongkir order campuran brand (tidak dialokasikan)<div className="font-serif text-lg mt-1">{fmtIDR(v.shared.shipping_unallocated_idr)}</div></div>
                        <div className="bg-white border border-mist rounded-sm p-3">Retur (terpisah, tidak dikurangkan)<div className="font-serif text-lg mt-1">{fmtIDR(v.shared.returns_idr)} · {v.shared.returns}</div></div>
                    </div>
                    <div>
                        <h3 className="text-[10px] uppercase tracking-[0.25em] text-neutral-400 mb-2">Pengecualian</h3>
                        <Exceptions items={v.exceptions} />
                    </div>
                </>
            )}
            {data?.history?.length > 0 && (
                <div className="text-xs text-neutral-500 space-y-0.5" data-testid="jv-history">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-400 mb-1">Riwayat verifikasi</div>
                    {data.history.map((h) => (
                        <div key={h.id}>{fmtDateTimeWIB(h.run_at)} · {h.run_by} · {h.ok ? Object.entries(h.statuses).map(([b, s]) => `${b}: ${s}`).join(", ") : `gagal — ${h.error}`}</div>
                    ))}
                </div>
            )}
        </div>
    );
}
